import type { Exercise, ExerciseTraits } from "./api";

/** Черновик формы: основная мышца может быть ещё не выбрана. */
export interface ExerciseDraft extends Omit<ExerciseTraits, "primary_muscle"> {
  primary_muscle: string | null;
}

/** Совпадает с MAX_SECONDARY на сервере. */
export const MAX_SECONDARY = 3;

export const emptyDraft = (name = ""): ExerciseDraft => ({
  name_ru: name,
  primary_muscle: null,
  secondary_muscles: [],
  equipment: "other",
  movement: "other",
});

export const draftOf = (exercise: Exercise): ExerciseDraft => ({
  name_ru: exercise.name_ru,
  primary_muscle: exercise.primary_muscle,
  secondary_muscles: [...exercise.secondary_muscles],
  equipment: exercise.equipment,
  movement: exercise.movement,
});

/** Выбрать основную: если она была среди вспомогательных — оттуда уходит. */
export const withPrimary = (draft: ExerciseDraft, muscle: string): ExerciseDraft => ({
  ...draft,
  primary_muscle: muscle,
  secondary_muscles: draft.secondary_muscles.filter((m) => m !== muscle),
});

/**
 * Включить или выключить вспомогательную. Основную вспомогательной не сделать,
 * четвёртую не добавить: сервер ответил бы отказом, лучше не дать нажать.
 */
export function toggleSecondary(draft: ExerciseDraft, muscle: string): ExerciseDraft {
  const on = draft.secondary_muscles.includes(muscle);
  if (on) {
    return { ...draft, secondary_muscles: draft.secondary_muscles.filter((m) => m !== muscle) };
  }
  if (muscle === draft.primary_muscle || draft.secondary_muscles.length >= MAX_SECONDARY) {
    return draft;
  }
  return { ...draft, secondary_muscles: [...draft.secondary_muscles, muscle] };
}

/** Что мешает сохранить — текстом для человека, или null. */
export function draftProblem(draft: ExerciseDraft): string | null {
  if (draft.name_ru.trim().length < 2) return "Название — хотя бы две буквы.";
  if (!draft.primary_muscle) return "Выберите основную мышцу.";
  return null;
}

/** Готовый к отправке набор характеристик. Вызывать после draftProblem. */
export const traitsOf = (draft: ExerciseDraft): ExerciseTraits => ({
  ...draft,
  name_ru: draft.name_ru.trim(),
  primary_muscle: draft.primary_muscle ?? "other",
});

/** Только изменённые поля: правка названия не должна перезаписывать остальное. */
export function patchOf(original: Exercise, draft: ExerciseDraft): Partial<ExerciseTraits> {
  const next = traitsOf(draft);
  const patch: Partial<ExerciseTraits> = {};
  if (next.name_ru !== original.name_ru) patch.name_ru = next.name_ru;
  if (next.primary_muscle !== original.primary_muscle) patch.primary_muscle = next.primary_muscle;
  if (next.equipment !== original.equipment) patch.equipment = next.equipment;
  if (next.movement !== original.movement) patch.movement = next.movement;
  if (next.secondary_muscles.join() !== original.secondary_muscles.join()) {
    patch.secondary_muscles = next.secondary_muscles;
  }
  return patch;
}
